import { getAuth } from "firebase/auth";
import { FirebaseError } from "firebase/app";

export const getAuthHeader = async () => {
  const auth = getAuth();
  const currentUser = auth.currentUser;

  const headers = {
    "Content-Type": "application/json",
  };

  if (currentUser) {
    const token = await currentUser.getIdToken();
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
};


/**
 * Maps Firebase error codes to user-friendly messages. 
 */
export function getFriendlyFirebaseErrorMessage(error, providerName = "") {
  if (error instanceof FirebaseError) {
    switch (error.code) {
      case "auth/invalid-email":
        return "The email address is not valid.";
      case "auth/user-disabled":
        return "This account has been disabled. Contact support.";
      case "auth/user-not-found":
        return "We couldn't find an account with this email. Double-check the address, or create an account to get started.";
      case "auth/wrong-password":
        return "Incorrect password. Try again, or use \"Forgot Password\" to reset it.";
      case "auth/too-many-requests":
        return "Too many failed attempts. Please wait and try again later.";
      case "auth/popup-closed-by-user":
        return `The ${providerName} login popup was closed before completing. Please try again.`;
      case "auth/cancelled-popup-request":
        return `Multiple popups detected. Please wait for the ${providerName} login to complete.`;
      case "auth/invalid-credential":
        // Firebase collapses "wrong password" and "no such account" into this
        // one code, so the copy has to cover both outcomes.
        return providerName
          ? `There was a problem verifying your ${providerName} credentials. Try again or use a different method.`
          : "That email and password don't match an account. Check your password, reset it below, or sign up if you're new here.";
      case "auth/network-request-failed":
        return "Network error. Please check your internet connection.";
      case "auth/internal-error":
        return "Something went wrong on our end. Please try again in a moment.";
      default:
        return `Unexpected error (${error.code}): ${error.message}`;
    }
  }

  return "An unknown error occurred. Please try again.";
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmailFormat(email) {
  return EMAIL_PATTERN.test(String(email || "").trim());
}

export function getFriendlyPasswordResetErrorMessage(error) {
  if (error instanceof FirebaseError) {
    switch (error.code) {
      case "auth/invalid-email":
        return "The email address is not valid. Please check and try again.";
      case "auth/user-not-found":
        return "If an account exists with this email, a password reset link has been sent.";
      case "auth/too-many-requests":
        return "Too many password reset requests. Please wait a few minutes and try again.";
      case "auth/network-request-failed":
        return "Network error. Please check your internet connection and try again.";
      default:
        return "Unable to send password reset email. Please try again.";
    }
  }
  return "Unable to send password reset email. Please try again.";
}

export function getPasswordResetContinueUrl() {
  if (typeof window !== "undefined" && window.location?.origin) {
    return `${window.location.origin}/login`;
  }
  return "https://civixthread.com/login";
}

export function getFriendlyFirebaseSignupErrorMessage(error, providerName = "") {
  if (error instanceof FirebaseError) {
    switch (error.code) {
      case "auth/email-already-in-use":
        return "An account with this email already exists. Try logging in instead.";
      case "auth/invalid-email":
        return "The email address is not valid.";
      case "auth/weak-password":
        return "Your password is too weak. Please choose a stronger one.";
      case "auth/popup-closed-by-user":
        return `The ${providerName} sign-up popup was closed before completing. Please try again.`;
      case "auth/cancelled-popup-request":
        return `Multiple popups detected. Please wait for the ${providerName} sign-up to complete.`;
      case "auth/network-request-failed":
        return "Network error. Please check your internet connection.";
      case "auth/internal-error":
        return "Something went wrong on our end. Please try again in a moment.";
      default:
        return `Unexpected error (${error.code}): ${error.message}`;
    }
  }

  // Axios/backend errors
  if (error?.response) {
    const { status, data } = error.response;
    const detail = typeof data?.detail === "string" ? data.detail.toLowerCase() : "";

    if (status === 400 || status === 409) {
      if (detail.includes("already exists")) {
        return "An account with this email already exists. Try logging in instead.";
      }
      if (detail.includes("invalid") || detail.includes("missing")) {
        return "There was a problem with the information you submitted. Please review and try again.";
      }
      return data?.detail || `Sign-up failed (code ${status}).`;
    }

    return `Sign-up failed. Server responded with status ${status}.`;
  }

  return error?.message || "Something went wrong during sign-up. Please try again.";
}

/**
 * User-facing message from axios/API errors without exposing raw network messages.
 */
export function getFriendlyApiError(
  error,
  fallback = "Something went wrong. Please try again."
) {
  const data = error?.response?.data;
  if (typeof data?.detail === "string" && data.detail.trim()) {
    return data.detail;
  }
  if (typeof data?.error === "string" && data.error.trim()) {
    return data.error;
  }
  if (typeof data?.message === "string" && data.message.trim()) {
    return data.message;
  }
  return fallback;
}